import { memo } from 'react';
import {
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter,
  ModalCloseButton,
  ModalConfirmButton,
} from '@/components/_ui/modal';
import { useCreateBulkTicketModal } from './use-create-bulk-ticket-modal';
import NewFileUpload from '@/features/_shared/components/file-handler/new-file-upload';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/_ui/react-hook-form';
import { Autocomplete } from '@/components/ui/_refactor/form/autocomplete';
import MessageError from '@/components/errors/message-error';
import { DownloadIcon } from 'lucide-react';
import { Spinner } from '@/components/_ui/spinner';
import { handleDomBubbleEvent } from '@/utils/dom';
import { Button } from '@/components/_ui/button';
import { type TColumn } from '@/features/ticketings/columns/column/types/column.type';

export type TCreateBulkTicketModalType = 'CreateBulkTicketModal';
export interface ICreateBulkTicketModalProps {
  spaceId: string;
}

const CreateBulkTicketModal = memo(({ spaceId }: ICreateBulkTicketModalProps) => {
  const {
    // Modal
    isCreateBulkTicketOpen,
    handleCloseModal,
    hasFile,

    // Form
    form,

    // File
    handleFileSelect,
    handleFileRemove,

    // Data
    listSpaceColumnsOptions,
    isLoadingListSpaceColumnsData,

    // Actions
    handleDownloadTemplate,
    handleSubmit,
    isUploading,

    // Download Template
    isDownloadingCreateBulkTicketTemplate,
  } = useCreateBulkTicketModal({ spaceId });

  return (
    <Modal
      isOpen={isCreateBulkTicketOpen}
      onClose={handleCloseModal}
      isCloseOutside={true}
      isButtonClose={true}
      className="md:w-[700px] w-[365px] p-[24px] pt-[42px] "
      lockBodyScroll
    >
      <ModalHeader className="border-b border-[#D9D9D9] pr-0">
        <div className="flex items-center justify-between gap-[42px] pb-[16px] pt-[0px]">
          <div className="flex flex-col gap-[8px]">
            <p className="text-[16px] font-[700] text-[#0C8DBF]">Bulk Ticket Creation</p>
            <p className="text-[12px] font-[400] text-[#9D9D9C]">
              Upload an XLSX file to import tickets. Make sure the file matches the required format.
            </p>
          </div>
          <div className="flex items-center gap-[8px] ">
            <Button
              size="sm"
              variant="secondary"
              disabled={isDownloadingCreateBulkTicketTemplate}
              className="gap-[8px]"
              type="button"
              onClick={(e) => {
                handleDomBubbleEvent(e);
                handleDownloadTemplate();
              }}
              endIcon={isDownloadingCreateBulkTicketTemplate ? <Spinner isLoading={true} className="w-[16px] h-[16px]" /> : <DownloadIcon className="w-[16px] h-[16px] flex-shrink-0" />}
            >
              Download Template
            </Button>
          </div>
        </div>
      </ModalHeader>

      <ModalContent className="px-[0px]">
        <Form {...form}>
          <form
            onSubmit={(e) => {
              handleDomBubbleEvent(e);
              form.handleSubmit(handleSubmit)(e);
            }}
            id="create-bulk-ticket-form"
            className="flex flex-col gap-[16px]"
          >
            <FormField
              control={form.control}
              name="spaceColumnId"
              render={({ field }) => {
                const error = form?.formState?.errors?.spaceColumnId?.message || '';
                const value = listSpaceColumnsOptions?.find((option) => option.spaceColumnId === field.value) || null;

                return (
                  <FormItem>
                    <div className="flex flex-col gap-[8px]">
                      <FormLabel className="flex items-center gap-[4px]">
                        <span className="text-[14px] font-[600] text-[#25151E]">Select Column</span>
                      </FormLabel>
                      <div className=" w-full">
                        <FormControl>
                          <Autocomplete
                            labelKey="name"
                            valueKey="spaceColumnId"
                            placeholder="Select column"
                            value={value}
                            disabled={isLoadingListSpaceColumnsData}
                            isLoading={isLoadingListSpaceColumnsData}
                            options={listSpaceColumnsOptions || []}
                            onChange={(selectedValue: TColumn | null) => {
                              form.setValue('spaceColumnId', selectedValue?.spaceColumnId ?? '', {
                                shouldValidate: true,
                               
                              });
                            }}
                            onClear={() => {
                              form.setValue('spaceColumnId', '', { shouldValidate: true });
                            }}
                          />
                        </FormControl>
                      </div>
                    </div>
                    <MessageError message={error} />
                  </FormItem>
                );
              }}
            />

            <div className="flex flex-col gap-[8px]">
              <NewFileUpload
                accept=".xlsx"
                maxSize={20 * 1024 * 1024} // 20MB
                maxFiles={1}
                initialFiles={[]}
                onFilesAdded={handleFileSelect}
                onFileRemoved={handleFileRemove}
              />
            </div>
          </form>
        </Form>
      </ModalContent>

      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleCloseModal();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="create-bulk-ticket-form" type="submit" disabled={ !form.formState.isValid}>
          {isUploading ? <Spinner className="w-[18px] h-[18px]" /> : null}
          {isUploading ? 'Uploading...' : 'Upload'}
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});
CreateBulkTicketModal.displayName = 'CreateBulkTicketModal';

export default CreateBulkTicketModal;
